import { useCallback, useEffect, useState } from 'react';
import { BOT_SKILL_LABELS } from '@tb/shared';
import type { BotSkill } from '@tb/shared';

import { Meter, Notice, Segmented, Stat, Tag } from '../ui/kit';
import { rememberPassword } from './gate';

// ---------------------------------------------------------------------------
// The simulation room's dashboard (D36): the switch that opens the game to the
// public or closes it, what is being played right now, and what has been
// played, from the server's telemetry store (packages/server/src/store/).
// Everything comes from /sim/admin/api, behind the simulation room's word.
// ---------------------------------------------------------------------------

type Window = '24h' | '7d' | '30d' | 'all';
type Kind = 'training' | 'battle_npc' | 'battle_players' | 'war' | 'sim' | 'custom';

/** The server's answer (admin.ts StatsAnswer), as far as this page reads it. */
interface Stats {
  window: Window;
  persistent: boolean;
  access: { open: boolean; changedAt: number | null };
  sound: { on: boolean; changedAt: number | null };
  live: { playersOnline: number; running: Partial<Record<Kind, number>> };
  headline: {
    gamesStarted: number; gamesFinished: number; gamesAbandoned: number; gamesRunning: number;
    uniquePlayers: number; visitors: number; playTimeMs: number; meanGameMs: number | null;
    completionRate: number | null; winRateVsNpcs: number | null;
  };
  byKind: { kind: Kind; started: number; finished: number; abandoned: number; running: number; meanMs: number | null; meanRounds: number | null; won: number; lost: number; draw: number; left: number }[];
  bySkill: { skill: string; games: number; won: number; lost: number; draw: number; left: number }[];
  training: { started: number; round2: number; round3: number; completed: number };
  players: { visitors: number; newPlayers: number; returning: number; gamesPerPlayer: number | null; sessions: number; meanSessionMs: number | null; devices: Record<'phone' | 'tablet' | 'desktop', number> };
  timeline: { bucketMs: number; buckets: { at: number; games: number }[] };
}

const KIND_LABEL: Record<Kind, string> = {
  training: 'Command training', battle_npc: 'Battle vs NPCs', battle_players: 'Battle vs commanders',
  war: 'War', sim: 'Simulation', custom: 'Custom room',
};
const WINDOWS: readonly { value: Window; label: string }[] = [
  { value: '24h', label: '24 hours' }, { value: '7d', label: '7 days' }, { value: '30d', label: '30 days' }, { value: 'all', label: 'All time' },
];
const POLL_MS = 15_000;

/** "1h 20m", "12m", "45s", or a dash. */
export function duration(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ${m % 60}m` : `${Math.round(h / 24)}d`;
}

const share = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const skillLabel = (s: string) => BOT_SKILL_LABELS[s as BotSkill] ?? s;
const when = (t: number | null) => (t == null ? 'never switched' : new Date(t).toLocaleString());

export function AdminDashboard({ password, onLocked }: { password: string; onLocked: () => void }) {
  const [window, setWindow] = useState<Window>('7d');
  const [testers, setTesters] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [switching, setSwitching] = useState(false);

  const call = useCallback(async (path: string, init: RequestInit = {}) => {
    const res = await fetch(`/sim/admin/api${path}`, {
      ...init, cache: 'no-store',
      headers: { 'x-sim-password': password, ...(init.body ? { 'content-type': 'application/json' } : {}) },
    });
    if (res.status === 403) { rememberPassword(null); onLocked(); throw new Error('locked'); }
    if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? `HTTP ${res.status}`);
    return res;
  }, [password, onLocked]);

  const load = useCallback(async () => {
    try {
      const res = await call(`/stats?window=${window}&testers=${testers ? 1 : 0}`);
      setStats(await res.json() as Stats);
      setProblem(null);
    } catch (e) {
      if ((e as Error).message !== 'locked') setProblem(`Could not load the figures: ${(e as Error).message}`);
    }
  }, [call, window, testers]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => { if (!document.hidden) void load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const flipSound = async () => {
    if (!stats) return;
    setSwitching(true);
    try {
      await call('/sound', { method: 'POST', body: JSON.stringify({ on: !stats.sound.on }) });
      await load();
    } catch (e) {
      if ((e as Error).message !== 'locked') setProblem(`Could not switch the sound: ${(e as Error).message}`);
    } finally {
      setSwitching(false);
    }
  };

  const flip = async () => {
    if (!stats) return;
    setSwitching(true);
    try {
      await call('/access', { method: 'POST', body: JSON.stringify({ open: !stats.access.open }) });
      setConfirming(false);
      await load();
    } catch (e) {
      if ((e as Error).message !== 'locked') setProblem(`Could not switch: ${(e as Error).message}`);
    } finally {
      setSwitching(false);
    }
  };

  const download = async (table: string) => {
    try {
      const res = await call(`/export.csv?table=${table}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `trading-blows-${table}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if ((e as Error).message !== 'locked') setProblem(`Could not export: ${(e as Error).message}`);
    }
  };

  if (!stats) return <div className="admin" data-testid="admin-dashboard">{problem ? <Notice tone="bad" inline>{problem}</Notice> : <p className="sub">Loading the figures…</p>}</div>;
  const h = stats.headline;
  const open = stats.access.open;
  const running = Object.entries(stats.live.running).filter(([, n]) => n);

  return <div className="admin" data-testid="admin-dashboard">
    <section className={`admin-access ${open ? 'open' : 'closed'}`} data-testid="admin-access" data-open={open}>
      <div className="admin-access-state">
        <Tag tone={open ? 'ok' : 'warn'}>{open ? 'Open' : 'Closed'}</Tag>
        <span>{open ? 'Training and battles are open to everyone.' : 'Training and battles are closed to the public: testers only.'} Wars stay locked to testers.</span>
      </div>
      <p className="sub">Switched {when(stats.access.changedAt)}.</p>
      {!confirming
        ? <button className={`btn ${open ? '' : 'primary'}`} onClick={() => setConfirming(true)} data-testid="admin-access-toggle">
          {open ? 'Close to the public' : 'Open to the public'}
        </button>
        : <div className="row wrap admin-confirm" role="group" aria-label="Confirm the switch">
          <span className="grow">{open ? 'Close the game? Games under way finish; nobody new gets in without the tester password.' : 'Open the game to everyone?'}</span>
          <button className="btn primary" disabled={switching} onClick={flip} data-testid="admin-access-confirm">{open ? 'Close it' : 'Open it'}</button>
          <button className="btn ghost" disabled={switching} onClick={() => setConfirming(false)}>Cancel</button>
        </div>}
      <p className="admin-live" data-testid="admin-live" role="status">
        Now: <b>{stats.live.playersOnline}</b> connected
        {running.length ? <> · {running.map(([k, n]) => `${KIND_LABEL[k as Kind]} ${n}`).join(' · ')}</> : ' · nothing being played'}
      </p>
    </section>

    <section className="admin-sound" data-testid="admin-sound" data-on={stats.sound.on}>
      <Tag tone={stats.sound.on ? 'ok' : 'plain'}>{stats.sound.on ? 'Sound on' : 'Sound off'}</Tag>
      <span className="grow">{stats.sound.on
        ? 'Every page plays the game sound; players can still mute it in Settings.'
        : 'No page plays any sound, and Settings says it is off for now.'} Switched {when(stats.sound.changedAt)}.</span>
      <button className="btn sm" disabled={switching} onClick={flipSound} data-testid="admin-sound-toggle">{stats.sound.on ? 'Turn sound off' : 'Turn sound on'}</button>
    </section>

    {!stats.persistent && <Notice tone="warn" inline data-testid="admin-memory">The store is in memory (no DATA_DIR): these figures and the switch reset when the server restarts.</Notice>}
    {problem && <Notice tone="bad" inline>{problem}</Notice>}

    <div className="row wrap admin-controls">
      <Segmented label="Period" value={window} options={WINDOWS} onChange={setWindow} size="compact" data-testid="admin-window" />
      <label className="row admin-testers"><input type="checkbox" checked={testers} onChange={(e) => setTesters(e.target.checked)} data-testid="admin-testers" /> Include tester games</label>
    </div>

    <div className="admin-tiles" data-testid="admin-headline">
      <Stat k="Games started" v={h.gamesStarted} />
      <Stat k="Finished" v={h.gamesFinished} unit={h.gamesRunning ? `· ${h.gamesRunning} running` : undefined} />
      <Stat k="Players" v={h.uniquePlayers} unit={`of ${h.visitors} visitors`} />
      <Stat k="Play time" v={duration(h.playTimeMs)} />
      <Stat k="Mean game" v={duration(h.meanGameMs)} />
      <Stat k="Completed" v={share(h.completionRate)} unit="of games ended" />
      <Stat k="Wins vs NPCs" v={share(h.winRateVsNpcs)} />
    </div>

    <Timeline stats={stats} />

    <h3 className="section-label">By mode</h3>
    <div className="admin-table-wrap"><table className="admin-table" data-testid="admin-by-mode">
      <thead><tr><th scope="col">Mode</th><th>Started</th><th>Finished</th><th>Abandoned</th><th>Mean length</th><th>Mean rounds</th><th>Won</th><th>Lost</th><th>Draw</th><th>Left</th></tr></thead>
      <tbody>{stats.byKind.length ? stats.byKind.map((k) => <tr key={k.kind}>
        <th scope="row">{KIND_LABEL[k.kind]}</th><td>{k.started}</td><td>{k.finished}</td><td>{k.abandoned}</td><td>{duration(k.meanMs)}</td>
        <td>{k.meanRounds == null ? '—' : k.meanRounds.toFixed(1)}</td><td>{k.won}</td><td>{k.lost}</td><td>{k.draw}</td><td>{k.left}</td>
      </tr>) : <tr><td colSpan={10} className="sub">No games in this period.</td></tr>}</tbody>
    </table></div>

    <div className="admin-columns">
      <section>
        <h3 className="section-label">Battles vs NPCs, by skill</h3>
        <div className="admin-table-wrap"><table className="admin-table" data-testid="admin-by-skill">
          <thead><tr><th scope="col">Against</th><th>Games</th><th>Won</th><th>Lost</th><th>Left</th><th>Win rate</th></tr></thead>
          <tbody>{stats.bySkill.length ? stats.bySkill.map((s) => <tr key={s.skill}>
            <th scope="row">{skillLabel(s.skill)}</th><td>{s.games}</td><td>{s.won}</td><td>{s.lost}</td><td>{s.left}</td>
            <td>{share(s.won + s.lost ? s.won / (s.won + s.lost) : null)}</td>
          </tr>) : <tr><td colSpan={6} className="sub">No battles against NPCs in this period.</td></tr>}</tbody>
        </table></div>
      </section>
      <section data-testid="admin-training">
        <h3 className="section-label">Command training</h3>
        {([['Started', stats.training.started], ['Reached round 2', stats.training.round2], ['Reached round 3', stats.training.round3], ['Completed', stats.training.completed]] as const).map(([k, n]) =>
          <Meter key={k} label={k} value={n} max={Math.max(1, stats.training.started)} tone="plain" text={`${n}`} size="compact" />)}
      </section>
      <section data-testid="admin-players">
        <h3 className="section-label">Players</h3>
        <dl className="admin-facts">
          <dt>Visitors</dt><dd>{stats.players.visitors}</dd>
          <dt>New</dt><dd>{stats.players.newPlayers}</dd>
          <dt>Came back another day</dt><dd>{stats.players.returning}</dd>
          <dt>Games per player</dt><dd>{stats.players.gamesPerPlayer == null ? '—' : stats.players.gamesPerPlayer.toFixed(1)}</dd>
          <dt>Sessions</dt><dd>{stats.players.sessions} · mean {duration(stats.players.meanSessionMs)}</dd>
          <dt>Devices</dt><dd>{stats.players.devices.phone} phone · {stats.players.devices.tablet} tablet · {stats.players.devices.desktop} desktop</dd>
        </dl>
      </section>
    </div>

    <h3 className="section-label">Export</h3>
    <div className="row wrap admin-exports">
      {['games', 'game_players', 'sessions', 'players'].map((t) => <button key={t} className="btn sm ghost" onClick={() => download(t)} data-testid={`admin-export-${t}`}>{t}.csv</button>)}
    </div>
    <p className="sub admin-note">Anonymous: each player is the id their browser keeps, never a name or an address. Simulations and tester games are left out of the headline unless included above.</p>
  </div>;
}

/** How many bars each period draws: every hour of a day, every day of a week or a month. */
const SPAN: Record<Window, number> = { '24h': 24, '7d': 7, '30d': 30, all: 0 };

/**
 * Games started per hour (the last 24 hours) or per day, as bars: always the
 * whole period, so one busy hour is one bar among its quiet neighbours. All
 * time runs from the first day anything was played, sixty days at most.
 */
function Timeline({ stats }: { stats: Stats }) {
  const { bucketMs, buckets } = stats.timeline;
  if (!buckets.length) return null;
  const last = Math.floor(Date.now() / bucketMs) * bucketMs;
  const span = SPAN[stats.window] || Math.round((last - buckets[0].at) / bucketMs) + 1;
  const n = Math.max(1, Math.min(60, span));
  const start = last - (n - 1) * bucketMs;
  const counts = Array.from({ length: n }, (_, i) => buckets.find((b) => b.at === start + i * bucketMs)?.games ?? 0);
  const top = Math.max(1, ...counts);
  const W = 600, H = 90, gap = 2, bw = Math.max(2, (W - gap * (n - 1)) / n);
  const label = (t: number) => bucketMs < 864e5 ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date(t).toLocaleDateString();
  return <figure className="admin-timeline" data-testid="admin-timeline">
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Games started per ${bucketMs < 864e5 ? 'hour' : 'day'}: ${counts.join(', ')}`}>
      {counts.map((c, i) => <rect key={i} x={i * (bw + gap)} y={H - (c / top) * (H - 4)} width={bw} height={(c / top) * (H - 4)}>
        <title>{`${label(start + i * bucketMs)}: ${c}`}</title>
      </rect>)}
    </svg>
    <figcaption className="sub">Games started per {bucketMs < 864e5 ? 'hour' : 'day'} · most {top}</figcaption>
  </figure>;
}
